import { SettingsPermissions } from "@redotech/account-common/permissions";
import { directMerchantAccess } from "../../settings-catalog/access";
import type {
  SettingGroupDefinition,
  SettingOwner,
  SettingRisk,
} from "../../settings-catalog/types";

export type InternationalCustomsFlatTeamSettingGroupId =
  | "international_customs.customs_artifact"
  | "international_customs.customs_metafields";

const groupOwner: SettingOwner = {
  kind: "teamSettings",
  product: "fulfillment",
};

const groupRisk: SettingRisk = {
  level: "low",
  reason: "International shipping settings including customs and markets.",
};

export const InternationalCustomsFlatTeamSettingGroups = [
  {
    id: "international_customs.customs_artifact",
    title: "Customs artifact",
    description:
      "Customs code settings in the Customs section of the International settings page.",
    productArea: "fulfillment",
    access: directMerchantAccess(
      SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
    ),
    owner: groupOwner,
    risk: groupRisk,
    agentEditability: { mode: "requiresApproval" },
    availability: {
      conditions: [
        {
          type: "settingEquals",
          settingId: "landedCost/showCustomsSettings",
          value: true,
        },
      ],
      unavailableBehavior: "hidden",
    },
    fullPageTarget: { type: "settings", path: "international/returns" },
    synonyms: ["customs artifact", "international customs"],
  },
  {
    id: "international_customs.customs_metafields",
    title: "Customs metafields",
    description:
      "Customs metafield settings in the Customs section of the International settings page.",
    productArea: "fulfillment",
    access: directMerchantAccess(
      SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
    ),
    owner: groupOwner,
    risk: groupRisk,
    agentEditability: { mode: "requiresApproval" },
    availability: {
      conditions: [
        {
          type: "settingEquals",
          settingId: "landedCost/showCustomsSettings",
          value: true,
        },
      ],
      unavailableBehavior: "hidden",
    },
    fullPageTarget: { type: "settings", path: "international/returns" },
    synonyms: ["customs metafields", "international customs"],
  },
] as const satisfies readonly SettingGroupDefinition<InternationalCustomsFlatTeamSettingGroupId>[];

export const InternationalCustomsFlatTeamSettingGroupById: Record<
  InternationalCustomsFlatTeamSettingGroupId,
  SettingGroupDefinition<InternationalCustomsFlatTeamSettingGroupId>
> = (() => {
  const map: Partial<
    Record<
      InternationalCustomsFlatTeamSettingGroupId,
      SettingGroupDefinition<InternationalCustomsFlatTeamSettingGroupId>
    >
  > = {};
  for (const group of InternationalCustomsFlatTeamSettingGroups) {
    map[group.id] = group;
  }
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  return map as Record<
    InternationalCustomsFlatTeamSettingGroupId,
    SettingGroupDefinition<InternationalCustomsFlatTeamSettingGroupId>
  >;
})();
